import type { ReactNode } from 'react';
import { Link } from 'react-router';

/**
 * The few marks a block's text can carry: **bold**, *italic*, ~~struck~~, `code` and [label](address).
 * The text is stored with the marks; it is shown formatted, and edited as it is written.
 */
export type Mark = 'b' | 'i' | 's' | 'code';
export interface Piece {
  /** What is shown. */
  text: string;
  marks: Mark[];
  href?: string;
  /** Where the shown text starts in the stored text, to put the cursor in the same place when editing. */
  at: number;
}

const OPEN: [string, Mark][] = [
  ['**', 'b'],
  ['~~', 's'],
  ['*', 'i'],
  ['`', 'code'],
];

/** Splits stored text into shown pieces. Unclosed marks are shown as they are. */
export function parseInline(src: string): Piece[] {
  const out: Piece[] = [];
  const walk = (from: number, to: number, marks: Mark[], href?: string) => {
    let buf = '';
    let bufAt = from;
    const flush = (at: number) => {
      if (buf) out.push({ text: buf, marks, ...(href ? { href } : {}), at: bufAt });
      buf = '';
      bufAt = at;
    };
    let i = from;
    while (i < to) {
      const c = src[i]!;
      // A link: [label](address), only outside code and other links.
      if (c === '[' && !href && !marks.includes('code')) {
        const close = findClose(src, i + 1, to, ']');
        if (close > 0 && src[close + 1] === '(') {
          const end = src.indexOf(')', close + 2);
          if (end > 0 && end < to && src.slice(close + 2, end).trim()) {
            flush(i);
            walk(i + 1, close, marks, src.slice(close + 2, end).trim());
            i = end + 1;
            bufAt = i;
            continue;
          }
        }
      }
      if (!marks.includes('code')) {
        const m = OPEN.find(([o]) => src.startsWith(o, i));
        if (m) {
          const [o, mark] = m;
          // A lone "*" next to a space is a star, not italic.
          const next = src[i + o.length];
          const close = next && next !== ' ' ? src.indexOf(o, i + o.length + (mark === 'code' ? 0 : 1)) : -1;
          if (close > 0 && close + o.length <= to && !(mark === 'i' && src[close - 1] === ' ')) {
            flush(i);
            walk(i + o.length, close, mark === 'code' ? [...marks, 'code'] : [...marks, mark], href);
            i = close + o.length;
            bufAt = i;
            continue;
          }
        }
      }
      if (!buf) bufAt = i;
      buf += c;
      i++;
    }
    flush(i);
  };
  walk(0, src.length, []);
  return out;
}

function findClose(s: string, from: number, to: number, ch: string) {
  for (let i = from; i < to; i++) if (s[i] === ch) return i;
  return -1;
}

const safeHref = (h: string) => /^(https?:\/\/|mailto:)/i.test(h) || /^\/(?!\/)/.test(h);

/** Shows stored text formatted. Each piece carries where it starts in the stored text. */
export function Inline({ text, onLink }: { text: string; onLink?: () => void }) {
  const pieces = parseInline(text);
  return (
    <>
      {pieces.map((p, i) => {
        let node: ReactNode = p.text;
        if (p.marks.includes('code')) node = <code>{node}</code>;
        if (p.marks.includes('s')) node = <s>{node}</s>;
        if (p.marks.includes('i')) node = <em>{node}</em>;
        if (p.marks.includes('b')) node = <strong>{node}</strong>;
        if (p.href && safeHref(p.href))
          node = p.href.startsWith('/') ? (
            <Link to={p.href} onClick={onLink}>
              {node}
            </Link>
          ) : (
            <a href={p.href} target="_blank" rel="noopener noreferrer">
              {node}
            </a>
          );
        return (
          <span key={i} data-at={p.at}>
            {node}
          </span>
        );
      })}
    </>
  );
}

/** The text without marks. */
export const plain = (text: string) =>
  parseInline(text)
    .map((p) => p.text)
    .join('');

/** Where the cursor goes in the stored text after a click in the shown text. */
export function storedOffset(root: HTMLElement): number | null {
  const sel = window.getSelection();
  if (!sel || !sel.anchorNode || !root.contains(sel.anchorNode)) return null;
  const span = (sel.anchorNode.nodeType === Node.TEXT_NODE ? sel.anchorNode.parentElement : (sel.anchorNode as HTMLElement))?.closest<HTMLElement>('[data-at]');
  if (!span) return null;
  // Count the characters of this piece before the click.
  let n = 0;
  const walker = document.createTreeWalker(span, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node === sel.anchorNode) return Number(span.dataset.at) + n + sel.anchorOffset;
    n += node.textContent?.length ?? 0;
  }
  return Number(span.dataset.at) + n;
}

/** Wraps the selected part of a text field in a mark, or takes the mark off when it is already there. */
export function toggleMark(value: string, start: number, end: number, open: string, close = open): { value: string; start: number; end: number } {
  const before = value.slice(0, start);
  const sel = value.slice(start, end);
  const after = value.slice(end);
  if (before.endsWith(open) && after.startsWith(close)) {
    return { value: before.slice(0, -open.length) + sel + after.slice(close.length), start: start - open.length, end: end - open.length };
  }
  if (sel.startsWith(open) && sel.endsWith(close) && sel.length >= open.length + close.length) {
    const inner = sel.slice(open.length, sel.length - close.length);
    return { value: before + inner + after, start, end: start + inner.length };
  }
  return { value: before + open + sel + close + after, start: start + open.length, end: end + open.length };
}
